import React, { useState, useEffect, useCallback } from "react";

import styles from "./ApproverList.module.css";
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface ApproverUser {
  userId: number;
  firstName?: string;
  lastName?: string;
  businessName?: string;
}

interface ApproverModel {
  id: number;
  approverId: number;
  approverRank: number;
  clientId?: number;
  projectId?: number;
}

interface ApproverListProps {
  entityType: "client" | "prospect";
  entityId: number;
  users: ApproverUser[];
  api: any;
  hideHeader?: boolean;
}

export default function ApproverList({ entityType, entityId, users, api, hideHeader }: ApproverListProps) {
  const [approvers, setApprovers] = useState<ApproverModel[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const isClient = entityType === "client";
  const basePath = isClient ? "ClientApprovers" : "ProjectApprovers";

  const resolveUserName = (approverId: number): string => {
    const u = users.find((u) => u.userId === approverId);
    if (!u) return `User #${approverId}`;
    const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    return full || u.businessName || `User #${approverId}`;
  };

  const loadApprovers = useCallback(async () => {
    if (!api || !entityId) { setApprovers([]); return; }
    try {
      const data = await api.get(`${basePath}/${entityId}`);
      const list: ApproverModel[] = Array.isArray(data) ? data : [];
      list.sort((a: ApproverModel, b: ApproverModel) => a.approverRank - b.approverRank);
      setApprovers(list);
    } catch (err: any) {
      if (err?.status === 404) {
        setApprovers([]);
      } else {
        console.error(`Error loading ${entityType} approvers:`, err);
      }
    }
  }, [api, entityId, basePath, entityType]);

  useEffect(() => {
    setApprovers([]);
    loadApprovers();
  }, [loadApprovers]);

  const availableUsers = users.filter(
    (u) => !approvers.some((a) => a.approverId === u.userId),
  );

  const addApprover = async () => {
    if (!api || !selectedUserId) return;
    setLoading(true);
    try {
      const body: any = {
        id: 0,
        approverId: Number(selectedUserId),
        approverRank: approvers.length + 1,
      };
      if (isClient) body.clientId = entityId;
      else body.projectId = entityId;

      await api.post(basePath, body);
      setSelectedUserId("");
      await loadApprovers();
      setToast({ message: "Approver added.", type: "success" });
    } catch (err) {
      console.error(`Error adding ${entityType} approver:`, err);
      setToast({ message: "Failed to add approver.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const removeApprover = async (recordId: number) => {
    if (!api) return;
    setLoading(true);
    try {
      await api.delete(`${basePath}/${recordId}`);
      await loadApprovers();
      setToast({ message: "Approver removed.", type: "success" });
    } catch (err) {
      console.error(`Error removing ${entityType} approver:`, err);
      setToast({ message: "Failed to remove approver.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const reorder = async (index: number, direction: "up" | "down") => {
    if (!api) return;
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= approvers.length) return;

    const updated = [...approvers];
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    const reranked = updated.map((a, i) => ({ ...a, approverRank: i + 1 }));

    setApprovers(reranked);
    setLoading(true);
    try {
      await api.put(`${basePath}/OrderChange/${entityId}`, reranked);
      await loadApprovers();
      setToast({ message: "Order updated.", type: "success" });
    } catch (err) {
      console.error(`Error reordering ${entityType} approvers:`, err);
      setToast({ message: "Failed to reorder approvers.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const label = isClient ? "Client" : "Prospect";

  return (
    <div className={styles["approver-card"]}>
      {!hideHeader && (
        <div className={styles["approver-section-head"]}>
          <h3 className={styles["approver-section-head__title"]}>{label} Approvers</h3>
          <p className={styles["approver-section-head__subtitle"]}>
            Manage the approval chain for this {entityType}.
          </p>
        </div>
      )}

      {entityId > 0 ? (
        <>
          <div className={styles["approver-composer"]}>
            <div className={styles["approver-field"]}>
              <label className={styles["approver-label"]} htmlFor={`approver-select-${entityType}`}>
                Add Approver
              </label>
              <SearchableSelect
                id={`approver-select-${entityType}`}
                className={styles["approver-control"]}
                options={availableUsers.map((u) => ({ value: u.userId, label: resolveUserName(u.userId) }))}
                value={selectedUserId ? Number(selectedUserId) : null}
                onChange={(val) => setSelectedUserId(val ? String(val) : "")}
                placeholder="Search users..."
                disabled={loading}
              />
            </div>

            <div className={styles["approver-actions"]}>
              <button
                type="button"
                className={styles["approver-btn"]}
                onClick={addApprover}
                disabled={!selectedUserId || loading}
              >
                + Add
              </button>
            </div>
          </div>

          <div className={styles["approver-subheader"]}>
            <span>Approvers</span>
          </div>

          {approvers.length === 0 ? (
            <div className={styles["approver-empty"]}>
              <svg className={styles["approver-empty__icon"]} width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="24" cy="16" r="8" stroke="#d1d5db" strokeWidth="2" />
                <path d="M10 40c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
                <path d="M32 28l4 4 6-8" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className={styles["approver-empty__title"]}>No approvers assigned</p>
              <p className={styles["approver-empty__subtitle"]}>
                {isClient
                  ? "Add a client approver above to require review of time entries before invoicing."
                  : "Add a prospect approver above to require review of time entries before invoicing."}
              </p>
            </div>
          ) : (
            <div className={styles["approver-table-wrap"]}>
              <table className={styles["approver-table"]}>
                <thead>
                  <tr>
                    <th className={styles["approver-col-rank"]}>#</th>
                    <th>Approver</th>
                    <th className={styles["approver-col-actions"]}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {approvers.map((a, i) => (
                    <tr key={a.id}>
                      <td className={styles["approver-rank"]}>{a.approverRank}</td>
                      <td className={styles["approver-name"]}>{resolveUserName(a.approverId)}</td>
                      <td>
                        <div className={styles["approver-row-actions"]}>
                          <button
                            type="button"
                            className={styles["approver-icon-btn"]}
                            onClick={() => reorder(i, "up")}
                            disabled={i === 0 || loading}
                            aria-label="Move up"
                            title="Move up"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className={styles["approver-icon-btn"]}
                            onClick={() => reorder(i, "down")}
                            disabled={i === approvers.length - 1 || loading}
                            aria-label="Move down"
                            title="Move down"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            className={`${styles["approver-icon-btn"]} ${styles["approver-icon-btn--danger"]}`}
                            onClick={() => removeApprover(a.id)}
                            disabled={loading}
                            aria-label={`Remove ${resolveUserName(a.approverId)}`}
                            title="Remove approver"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className={styles["approver-empty"]}>
          Save the {entityType} before managing approvers.
        </div>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
